import { ChevronDown } from "@/components/icons";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { CalendarEventType, CalendarImportance } from "./types";

const ALL_IMPORTANCE: CalendarImportance[] = ["high", "medium", "low"];
const ALL_TYPES: CalendarEventType[] = [
  "econ_release",
  "central_bank",
  "gov_publication",
  "exec_schedule",
  "gov_release",
  "holiday",
  "other",
];

const TYPE_LABEL: Record<CalendarEventType, string> = {
  econ_release: "Economic release",
  central_bank: "Central bank",
  gov_publication: "Govt publication",
  exec_schedule: "Exec schedule",
  gov_release: "Govt release",
  holiday: "Holiday",
  other: "Other",
};

interface FilterState {
  materials?: string[];
  importance?: CalendarImportance[];
  types?: CalendarEventType[];
}

interface FilterBarProps {
  materials: string[];
  selectedMaterials: string[];
  selectedImportance: readonly CalendarImportance[];
  selectedTypes: readonly CalendarEventType[];
  counts: { total: number; past: number; upcoming: number };
  onChange: (next: FilterState) => void;
}

function toggle<T>(arr: readonly T[], v: T): T[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

/**
 * Summarize a checkbox-dropdown's current selection for the trigger button:
 * "All" when everything is on, "None" when off, "N of M" otherwise.
 */
function summarize(selected: number, total: number): string {
  if (total === 0) return "None";
  if (selected === total) return "All";
  if (selected === 0) return "None";
  return `${selected} of ${total}`;
}

interface CheckboxDropdownProps<T extends string> {
  label: string;
  options: readonly T[];
  selected: readonly T[];
  renderLabel?: (option: T) => string;
  onChange: (next: T[]) => void;
}

function CheckboxDropdown<T extends string>({
  label,
  options,
  selected,
  renderLabel,
  onChange,
}: CheckboxDropdownProps<T>) {
  const allChecked = options.every((o) => selected.includes(o));
  const summary = summarize(selected.length, options.length);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs",
          "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
      >
        <span className="text-muted-foreground">{label}:</span>
        <span className={cn(allChecked ? "text-foreground/90" : "text-sky-400 font-medium")}>
          {summary}
        </span>
        <ChevronDown className="h-3 w-3 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-48">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {label}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((opt) => (
          <DropdownMenuCheckboxItem
            key={opt}
            checked={selected.includes(opt)}
            // Radix passes the new boolean; we onCheckedChange-flip via toggle.
            onSelect={(event) => {
              event.preventDefault();
              onChange(toggle(selected, opt));
            }}
          >
            {renderLabel ? renderLabel(opt) : opt}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault();
            onChange(allChecked ? [] : [...options]);
          }}
          className="text-xs text-muted-foreground"
        >
          {allChecked ? "Clear all" : "Select all"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function FilterBar({
  materials,
  selectedMaterials,
  selectedImportance,
  selectedTypes,
  counts,
  onChange,
}: FilterBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <CheckboxDropdown<string>
        label="Materials"
        options={materials}
        selected={selectedMaterials}
        onChange={(next) => onChange({ materials: next })}
      />

      {/* Importance stays as inline color pills — only three options, the
          colors carry the meaning, and putting it behind a dropdown would
          hide useful at-a-glance state. */}
      <span className="ms-1 text-muted-foreground">Importance:</span>
      {ALL_IMPORTANCE.map((imp) => {
        const on = selectedImportance.includes(imp);
        const palette =
          imp === "high"
            ? "border-red-500/60 bg-red-500/10 text-red-400"
            : imp === "medium"
              ? "border-amber-500/60 bg-amber-500/10 text-amber-400"
              : "border-emerald-500/60 bg-emerald-500/10 text-emerald-400";
        return (
          <button
            key={imp}
            type="button"
            onClick={() =>
              onChange({ importance: toggle(selectedImportance, imp) })
            }
            className={cn(
              "rounded-full border px-2 py-0.5 capitalize",
              on
                ? palette
                : "border-border text-muted-foreground line-through opacity-60",
            )}
          >
            {imp}
          </button>
        );
      })}

      <CheckboxDropdown<CalendarEventType>
        label="Type"
        options={ALL_TYPES}
        selected={selectedTypes}
        renderLabel={(t) => TYPE_LABEL[t]}
        onChange={(next) => onChange({ types: next })}
      />

      <span className="ms-auto text-muted-foreground tabular-nums">
        {counts.total} events · {counts.past} past · {counts.upcoming} upcoming
      </span>
    </div>
  );
}
